import type { Metadata } from "next";
import { brand } from "@/config/brand";
import { getMeta } from "./meta";
import { getMessage } from "@/lib/i18n-messages";
import { supportedLocales, isNonLocalizedPath } from "@/lib/i18n";
import { brands, normalizeBrandText } from "@/config/brands";
import { getCanonicalBase } from "@/config/brands/brand-tech";
import { getBrandMetaTemplate } from "../brand-meta";
import { resolveSeoRoutePolicy, routeFamilyForPath } from "@/config/brands/seo-policy";

// All registered brand display names + short names. Used to strip ANY known
// brand suffix from content-authored titles (meta.json bakes in "eSIMSupra"),
// so a non-default active brand does not inherit a foreign brand in its title.
const KNOWN_BRAND_NAMES: string[] = Array.from(
  new Set(
    Object.values(brands).flatMap((b) =>
      b ? [b.legacy.brand.displayName, b.legacy.brand.name] : [],
    ),
  ),
).filter(Boolean) as string[];

function stripTrailingBrand(value: string | undefined, siteName: string) {
  if (!value) return value;
  // Strip a trailing "| <brand>" for the active siteName OR any other known
  // brand, looping to handle stacked suffixes like "... | eSIMSupra | eSIMCobra".
  // Regex-free to keep brand names with special chars safe.
  const names = Array.from(new Set([siteName, ...KNOWN_BRAND_NAMES])).filter(Boolean);
  let out = value.trim();
  let changed = true;
  while (changed) {
    changed = false;
    for (const n of names) {
      if (out.toLowerCase().endsWith(n.toLowerCase())) {
        const before = out.slice(0, out.length - n.length).trimEnd();
        if (before.endsWith("|")) {
          out = before.slice(0, -1).trimEnd();
          changed = true;
        }
      }
    }
  }
  return out;
}

// Map ISO 639-1 locales to OpenGraph locale codes (BCP-47-ish).
// Per OG spec og:locale should be language_TERRITORY format.
const OG_LOCALES: Record<string, string> = {
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  de: 'de_DE',
  it: 'it_IT',
  pt: 'pt_PT',
  ar: 'ar_SA',
};

// hreflang locale set. MUST match the locales actually generated by
// generateStaticParams (lib/i18n → supportedLocales). Deriving from OG_LOCALES
// previously emitted hreflang for it/pt — locales that are NOT built — producing
// 404 return-tags that invalidate the whole hreflang cluster. Single source of
// truth: supportedLocales.
const HREFLANG_LOCALES = supportedLocales as readonly string[];

/**
 * Build a route's localized URL. English stays at root, others get a /<locale> prefix.
 */
function localizeRoute(route: string, locale: string): string {
  const cleanRoute = route || '/';
  if (!locale || locale === 'en') return cleanRoute;
  if (cleanRoute === '/') return `/${locale}/`;
  return `/${locale}${cleanRoute}`;
}

/**
 * Look up a translated meta string from the i18n bundle. Falls back to the
 * English value from meta.json if the locale doesn't have an override.
 *
 * Convention: i18n keys for meta are at `meta.<routeKey>.<field>` where
 * routeKey is the same key used in meta.json (e.g., 'home', 'faq').
 */
function localizedMetaField(
  route: string,
  field: 'title' | 'description' | 'ogTitle' | 'ogDescription',
  englishValue: string | undefined,
  locale: string,
): string | undefined {
  if (!locale || locale === 'en') return englishValue;
  const routeKey = route === '/' ? 'home' : route.replace(/^\//, '').replace(/\/$/, '');
  const i18nKey = `meta.${routeKey}.${field}`;
  return getMessage(locale as any, i18nKey, englishValue);
}

/**
 * Rebase a possibly-absolute asset URL onto the active brand's canonical base.
 * meta.json bakes in absolute esimsupra.com asset URLs (og image / logo). For a
 * non-default brand we keep the path but swap the origin to the brand domain.
 * Relative paths are returned as-is (they resolve against metadataBase). For the
 * default brand the origin is already esimsupra.com, so output is unchanged.
 */
function rebaseAsset(value: string | undefined, base: URL): string | undefined {
  if (!value) return value;
  if (/^https?:\/\//i.test(value)) {
    try {
      const u = new URL(value);
      return `${base.origin}${u.pathname}${u.search}${u.hash}`;
    } catch {
      return value;
    }
  }
  return value;
}

/**
 * Build Next.js Metadata for a route, optionally localized.
 *
 * @param route - the page route, e.g. "/", "/faq/", "/destinations/jp/"
 * @param locale - optional ISO 639-1 locale. When omitted or 'en', behavior
 *                 matches the original English-only buildMetadata.
 *
 * For non-English locales, this:
 *   - Sets canonical to https://esimsupra.com/<locale><route> (critical for
 *     Google to index the locale page as its own URL, not as a duplicate)
 *   - Sets og:url to the same locale-aware URL
 *   - Sets og:locale to the BCP-47 form (es_ES, fr_FR, etc.)
 *   - Looks up localized title/description/ogTitle/ogDescription from i18n
 *     bundles, falling back to English meta.json values if missing
 */
export function buildMetadata(
  route: string,
  locale?: string,
  overrides?: {
    title?: string;
    description?: string;
    ogTitle?: string;
    ogDescription?: string;
    keywords?: string;
  },
): Metadata {
  const m = getMeta(route);
  const siteName = brand.brand.displayName || brand.brand.name;
  // Canonical base = ACTIVE brand's canonical domain (getCanonicalBase). For the
  // default brand this is https://esimsupra.com, so output stays byte-identical;
  // for other brands every canonical/og/hreflang URL now uses the brand domain
  // instead of the esimsupra.com values baked into meta.json.
  const base = new URL(getCanonicalBase());
  const lang = locale && HREFLANG_LOCALES.includes(locale) ? locale : 'en';
  const localizedRoute = localizeRoute(route, lang);

  // Always derive canonical from the active brand base + route. We intentionally
  // do NOT trust m.canonical (it hardcodes esimsupra.com). For the default brand
  // `new URL(localizedRoute, base)` reproduces the exact meta.json canonical
  // strings (verified: same domain, same trailing slash), so default output is
  // unchanged.
  const canonical = new URL(localizedRoute, base).toString();
  // og:image — if meta.json supplied an absolute esimsupra.com image, rebase it
  // onto the active brand domain; relative paths resolve against metadataBase.
  const ogImage = rebaseAsset(m.ogImage, base) || "/og-image.jpg";

  // Per-brand metadata template (Goal 2 / masterprompt §10). For a non-default
  // brand with a template for this route class, its DISTINCT title/description
  // replaces the shared meta.json copy as the English BASE. Localized pages
  // still override via i18n (localizedMetaField), and the default brand has no
  // template (tpl === undefined ⇒ shared meta.json copy ⇒ byte-identical output).
  //
  // `overrides` (highest precedence) lets a dynamic route supply per-instance
  // copy — e.g. product pages, which have no meta.json entry and would otherwise
  // inherit the "home" fallback (getMeta) producing a generic homepage title.
  // When overrides is undefined (every existing caller) output is byte-identical.
  const tpl = getBrandMetaTemplate(route);
  const baseTitle = overrides?.title ?? tpl?.title ?? m.title;
  const baseDescription = overrides?.description ?? tpl?.description ?? m.description;
  const baseOgTitle = overrides?.ogTitle ?? overrides?.title ?? tpl?.ogTitle ?? tpl?.title ?? m.ogTitle;
  const baseOgDescription =
    overrides?.ogDescription ?? overrides?.description ?? tpl?.ogDescription ?? tpl?.description ?? m.ogDescription ?? m.description;
  const baseKeywords = overrides?.keywords ?? tpl?.keywords ?? m.keywords;

  const englishTitle = stripTrailingBrand(normalizeBrandText(baseTitle), siteName) || siteName;
  const englishOgTitle = stripTrailingBrand(normalizeBrandText(baseOgTitle), siteName) || englishTitle;

  // Strip trailing brand from localized titles to avoid duplicate suffixes (B1)
  let title = normalizeBrandText(localizedMetaField(route, 'title', englishTitle, lang) ?? englishTitle) ?? englishTitle;
  title = stripTrailingBrand(title, siteName) || siteName;

  const description = normalizeBrandText(localizedMetaField(route, 'description', baseDescription, lang));

  let ogTitle = normalizeBrandText(localizedMetaField(route, 'ogTitle', englishOgTitle, lang) ?? englishOgTitle) ?? englishOgTitle;
  ogTitle = stripTrailingBrand(ogTitle, siteName) || title;

  const ogDescription = normalizeBrandText(localizedMetaField(
    route,
    'ogDescription',
    baseOgDescription,
    lang,
  ));

  // Build hreflang language alternates for every supported locale.
  // Google requires self-reference + bidirectional links; x-default points
  // to the English root as the canonical fallback.
  //
  // EXCEPTION: English-only (non-localized) routes — /product, /devices,
  // /carriers, /authors, /blog/<slug>, etc. (lib/i18n NON_LOCALIZED_PREFIXES)
  // have NO localized counterparts. Emitting hreflang for them points return
  // tags at /<locale>/product/... URLs that are never built and serve
  // not-found, which invalidates the whole hreflang cluster (same failure
  // class as the prior it/pt leak). For these routes we emit no hreflang
  // map — just the self-referencing English canonical above.
  const languages: Record<string, string> = {};
  if (!isNonLocalizedPath(route) && !m.noindex && !(typeof m.robots === 'object' && m.robots?.index === false) && resolveSeoRoutePolicy(routeFamilyForPath(route)).shouldIndexRoute) {
    for (const loc of HREFLANG_LOCALES) {
      languages[loc] = new URL(localizeRoute(route, loc), base).toString();
    }
    languages['x-default'] = new URL(localizeRoute(route, 'en'), base).toString();
  }

  return {
    metadataBase: base,
    title: {
      absolute: title.endsWith(` | ${siteName}`) ? title : `${title} | ${siteName}`,
      template: `%s | ${siteName}`,
    },
    description,
    keywords: normalizeBrandText(baseKeywords)?.split(",")?.map((k) => k.trim()).filter(Boolean),
    alternates: {
      canonical,
      languages: Object.keys(languages).length > 0 ? languages : undefined,
    },
    robots: (() => {
      // Provisional-domain safety (forget-proof default). We must NEVER index a
      // brand that is still served from an unowned/preview domain. Two layers:
      //   1. Explicit: tech.robotsPolicy === 'noindex-preview' → noindex.
      //   2. Safe default: any brand that is NOT the owned default brand
      //      (esimsupra) and has NOT explicitly declared tech.domainStatus ===
      //      'owned' is treated as provisional → noindex. This means a new brand
      //      cannot accidentally get indexed on the wrong domain just because
      //      someone forgot to set robotsPolicy. To make a brand indexable you
      //      must consciously set domainStatus:'owned' (done once its real
      //      domain is registered + pointed here).
      // Safety overrides any page-level index directive; a page that sets
      // noindex stays noindex either way. The default brand is unaffected
      // (it IS the default ⇒ indexable ⇒ output byte-identical).
      const policy = resolveSeoRoutePolicy(routeFamilyForPath(route));
      if (!policy.shouldIndexRoute) return { index: false, follow: false };
      return m.robots || (m.noindex ? { index: false, follow: false } : undefined);
    })(),
    openGraph: {
      type: (m.ogType as any) || "website",
      url: canonical,
      title: ogTitle,
      description: ogDescription,
      siteName,
      locale: OG_LOCALES[lang] || OG_LOCALES.en,
      images: [{ url: ogImage, width: 1200, height: 630, alt: ogTitle }],
    },
    twitter: {
      card: (m.twitterCard as any) || "summary_large_image",
      title: ogTitle,
      description: ogDescription,
      images: [ogImage],
      creator: brand.social.twitter?.includes("@") ? (brand.social.twitter as any) : undefined,
    },
    icons: {
      icon: [
        { url: "/favicon-96x96.png", type: "image/png", sizes: "96x96" },
        { url: "/favicon.svg", type: "image/svg+xml" },
        { url: "/favicon.ico", rel: "shortcut icon" },
      ],
      shortcut: "/favicon.ico",
      apple: [{ url: "/apple-touch-icon.png", sizes: "180x180" }],
    },
    manifest: "/site.webmanifest",
    appleWebApp: {
      title: siteName,
    },
    other: {
      "llms-txt": `${base.origin}/llms.txt`,
    },
  };
}
